import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AIProgramCreator from '../components/PurchaseProgram/AIProgramCreator';
import { splitCsv } from '../components/PurchaseProgram/classicCommerceScopeUtils';
import ProgramCreationWizard from '../components/PurchaseProgram/ProgramCreationWizard';
import { createAdminProgram, getCommerceContext, getCommerceUsers, listProductLists, listUserGroups } from '../services/purchaseProgramApi';

// ─── Mode picker constants ─────────────────────────────────────────────────────
const MODES = [
  { key: 'ai',      icon: 'bi-stars',          color: '#6366f1', bg: '#ede9fe', title: 'AI Assistant',   desc: 'Chat with an AI that asks the right questions and builds the program for you. Conversation history saved — resume any time.' },
  { key: 'wizard',  icon: 'bi-ui-checks-grid', color: '#0891b2', bg: '#e0f2fe', title: 'Guided Wizard',  desc: 'Step-by-step questions with smart defaults and suggested options. Great for new programs.' },
];

// ─── Scoping preflight — site (ship-to) → customers → review (users preview) ─
const SCOPE_STEPS = [
  { key: 'site', label: 'Site', icon: 'bi-geo-alt' },
  { key: 'customer', label: 'Customers', icon: 'bi-building' },
  { key: 'basis', label: 'Scope type', icon: 'bi-funnel' },
  { key: 'eligibility', label: 'Users / addresses', icon: 'bi-person-badge' },
  { key: 'review', label: 'Review', icon: 'bi-check2-circle' },
];

function ScopingPreflight({ onConfirm }) {
  const [step, setStep] = useState('site');
  const [sites, setSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(true);
  const [selectedSite, setSelectedSite] = useState(null);
  const [siteSearch, setSiteSearch] = useState('');

  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');

  const [loadingUsers, setLoadingUsers] = useState(false);
  const [customerUsers, setCustomerUsers] = useState({});
  const [reviewUserSearch, setReviewUserSearch] = useState('');
  /** 'all' = any enrolled user under selected customers; 'select' = only chosen CI users */
  const [userScopeMode, setUserScopeMode] = useState('all');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userPickSearch, setUserPickSearch] = useState('');
  const [restrictionBasis, setRestrictionBasis] = useState('users');
  const [deliveryScopeMode, setDeliveryScopeMode] = useState('all');
  const [deliveryShipToCsv, setDeliveryShipToCsv] = useState('');
  const [deliveryPickSearch, setDeliveryPickSearch] = useState('');

  useEffect(() => {
    setLoadingSites(true);
    getCommerceContext().then(data => {
      const allSites = [...(data?.sites || [])].sort((a, b) => {
        const aOn = a.is_active !== false;
        const bOn = b.is_active !== false;
        if (aOn !== bOn) return aOn ? -1 : 1;
        return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
      });
      setSites(allSites);
      if (allSites.length === 1) {
        setSelectedSite(allSites[0]);
        loadCustomersForSite(allSites[0].id);
      }
    }).catch((err) => {
      toast.error(err?.message || 'Could not load ExtremeB2B sites');
    }).finally(() => setLoadingSites(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomersForSite = (siteId) => {
    setLoadingCustomers(true);
    getCommerceContext(siteId).then(data => {
      const cList = data?.customers || [];
      setCustomers(cList);
      if (cList.length === 1) setSelectedCustomers([cList[0]]);
    }).catch(() => {}).finally(() => setLoadingCustomers(false));
  };

  const filteredSites = useMemo(() => {
    const q = siteSearch.trim().toLowerCase();
    if (!q) return sites;
    return sites.filter(s =>
      (s.name || '').toLowerCase().includes(q)
      || (s.url || '').toLowerCase().includes(q)
      || String(s.id || '').toLowerCase().includes(q),
    );
  }, [sites, siteSearch]);

  const handleSiteSelect = (site) => {
    setSelectedSite(site);
    setSelectedCustomers([]);
    setCustomerUsers({});
    setSiteSearch('');
    setUserScopeMode('all');
    setSelectedUsers([]);
    setUserPickSearch('');
    setRestrictionBasis('users');
    setDeliveryScopeMode('all');
    setDeliveryShipToCsv('');
    setDeliveryPickSearch('');
    loadCustomersForSite(site.id);
    setStep('customer');
  };

  const fetchUsersForCustomer = (custId) => {
    if (customerUsers[custId]) return;
    setLoadingUsers(true);
    getCommerceUsers(custId).then(data => {
      setCustomerUsers(u => ({ ...u, [custId]: data }));
    }).catch(() => {}).finally(() => setLoadingUsers(false));
  };

  const toggleCustomer = (cust) => {
    setSelectedCustomers(prev => {
      const exists = prev.some(c => c.id === cust.id);
      if (exists) return prev.filter(c => c.id !== cust.id);
      const next = [...prev, cust];
      if (!customerUsers[cust.id]) fetchUsersForCustomer(cust.id);
      return next;
    });
  };

  const filteredCustomers = useMemo(() => customers.filter(c => {
    if (!customerSearch) return true;
    const q = customerSearch.toLowerCase();
    return (c.name || '').toLowerCase().includes(q)
      || (c.erp_customer_id || '').toLowerCase().includes(q)
      || (c.email || '').toLowerCase().includes(q);
  }), [customers, customerSearch]);

  const selectAllFilteredCustomers = () => {
    const ids = new Set(selectedCustomers.map(c => c.id));
    filteredCustomers.forEach(cust => {
      if (!ids.has(cust.id)) {
        ids.add(cust.id);
        if (!customerUsers[cust.id]) fetchUsersForCustomer(cust.id);
      }
    });
    const merged = [...selectedCustomers];
    filteredCustomers.forEach(cust => {
      if (!merged.some(c => c.id === cust.id)) merged.push(cust);
    });
    setSelectedCustomers(merged);
  };

  const clearCustomerSelection = () => {
    setSelectedCustomers([]);
    setUserScopeMode('all');
    setSelectedUsers([]);
    setUserPickSearch('');
    setDeliveryScopeMode('all');
    setDeliveryShipToCsv('');
    setDeliveryPickSearch('');
  };

  const usersForPicker = useMemo(() => {
    const rows = [];
    selectedCustomers.forEach(c => {
      const pack = customerUsers[c.id];
      (pack?.users || []).forEach(u => {
        const displayName = u.display_name
          || [u.first_name, u.last_name].filter(Boolean).join(' ')
          || u.email
          || '';
        rows.push({
          id: u.id,
          customer_id: c.id,
          customer_name: c.name,
          display_name: displayName,
          email: u.email || '',
        });
      });
    });
    rows.sort((a, b) => (a.display_name || '').localeCompare(b.display_name || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [selectedCustomers, customerUsers]);

  useEffect(() => {
    setSelectedUsers(prev => prev.filter(u => selectedCustomers.some(c => c.id === u.customer_id)));
  }, [selectedCustomers]);

  const filteredUsersForPicker = useMemo(() => {
    const q = userPickSearch.trim().toLowerCase();
    if (!q) return usersForPicker;
    return usersForPicker.filter(u =>
      (u.display_name || '').toLowerCase().includes(q)
      || (u.email || '').toLowerCase().includes(q)
      || (u.customer_name || '').toLowerCase().includes(q),
    );
  }, [usersForPicker, userPickSearch]);

  const addressesForPicker = useMemo(() => {
    const rows = [];
    selectedCustomers.forEach((c) => {
      const pack = customerUsers[c.id];
      (pack?.addresses || []).forEach((a) => {
        const line = [a.city, a.state_province].filter(Boolean).join(', ');
        rows.push({
          id: a.id,
          customer_id: c.id,
          customer_name: c.name,
          label: a.label || 'Address',
          summary: `${line}${a.postal_code ? ` ${a.postal_code}` : ''}`.trim(),
        });
      });
    });
    rows.sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [selectedCustomers, customerUsers]);

  const filteredAddrPick = useMemo(() => {
    const q = deliveryPickSearch.trim().toLowerCase();
    if (!q) return addressesForPicker;
    return addressesForPicker.filter(
      (a) =>
        (a.label || '').toLowerCase().includes(q)
        || (a.summary || '').toLowerCase().includes(q)
        || (a.customer_name || '').toLowerCase().includes(q),
    );
  }, [addressesForPicker, deliveryPickSearch]);

  const selectedDeliveryIds = useMemo(() => splitCsv(deliveryShipToCsv), [deliveryShipToCsv]);

  const togglePickUser = (row) => {
    setSelectedUsers(prev => {
      const exists = prev.some(p => p.id === row.id);
      if (exists) return prev.filter(p => p.id !== row.id);
      return [...prev, { id: row.id, customer_id: row.customer_id }];
    });
  };

  const selectAllFilteredUsers = () => {
    const ids = new Set(selectedUsers.map(u => u.id));
    const add = filteredUsersForPicker.filter(u => !ids.has(u.id)).map(u => ({ id: u.id, customer_id: u.customer_id }));
    setSelectedUsers(prev => [...prev, ...add]);
  };

  const clearUserPick = () => setSelectedUsers([]);

  const togglePickAddr = (row) => {
    const id = String(row.id);
    const picked = new Set(selectedDeliveryIds);
    if (picked.has(id)) picked.delete(id);
    else picked.add(id);
    setDeliveryShipToCsv([...picked].join(', '));
  };

  const selectAllFilteredAddr = () => {
    const picked = new Set(selectedDeliveryIds);
    filteredAddrPick.forEach((row) => picked.add(String(row.id)));
    setDeliveryShipToCsv([...picked].join(', '));
  };

  const clearAddrPick = () => setDeliveryShipToCsv('');

  const continueFromCustomer = () => setStep('basis');

  const continueFromBasis = () => setStep('eligibility');

  const continueFromEligibility = () => {
    if (restrictionBasis === 'users') {
      if (userScopeMode === 'select' && selectedCustomers.length > 0 && selectedUsers.length === 0) {
        toast.error('Select at least one user, or choose “All enrolled users”.');
        return;
      }
    } else if (restrictionBasis === 'delivery') {
      if (deliveryScopeMode === 'select' && selectedDeliveryIds.length === 0) {
        toast.error('Select at least one ship-to address, or choose “All saved addresses”.');
        return;
      }
    }
    setStep('review');
  };

  useEffect(() => {
    if (step !== 'eligibility' || selectedCustomers.length === 0) return;
    selectedCustomers.forEach((c) => {
      if (!customerUsers[c.id]) {
        getCommerceUsers(c.id).then((data) => {
          setCustomerUsers((u) => ({ ...u, [c.id]: data }));
        }).catch(() => {});
      }
    });
  }, [step, selectedCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 'review' || selectedCustomers.length === 0) return;
    selectedCustomers.forEach(c => {
      if (!customerUsers[c.id]) {
        getCommerceUsers(c.id).then(data => {
          setCustomerUsers(u => ({ ...u, [c.id]: data }));
        }).catch(() => {});
      }
    });
  }, [step, selectedCustomers]); // eslint-disable-line react-hooks/exhaustive-deps

  const reviewCoverageSummary = useMemo(() => {
    let userCount = 0;
    let addrCount = 0;
    selectedCustomers.forEach(c => {
      const pack = customerUsers[c.id];
      if (pack) {
        userCount += pack.users?.length || 0;
        addrCount += pack.addresses?.length || 0;
      }
    });
    return { userCount, addrCount };
  }, [selectedCustomers, customerUsers]);

  const handleConfirm = () => {
    const customerIds = selectedCustomers.map(c => c.id).join(', ');
    const siteId = selectedSite?.id || '';
    onConfirm({
      customer_csv: customerIds,
      ship_to_csv: siteId,
      selectedSite,
      selectedCustomers,
      customerUsersMap: customerUsers,
      userScopeMode,
      selectedUsers: restrictionBasis === 'users' && userScopeMode === 'select' ? selectedUsers : [],
      restriction_basis: restrictionBasis,
      delivery_scope_mode: deliveryScopeMode,
      delivery_ship_to_csv: restrictionBasis === 'delivery' ? deliveryShipToCsv : '',
    });
  };

  const cardBase = {
    background: '#fff', border: '2px solid #e5e7eb', borderRadius: 12, padding: '14px 16px',
    cursor: 'pointer', transition: 'all .15s', width: '100%', textAlign: 'left',
  };
  const cardActive = { ...cardBase, borderColor: '#1a56db', background: '#eff6ff', boxShadow: '0 0 0 3px rgba(26,86,219,.12)' };

  const stepIndex = SCOPE_STEPS.findIndex(s => s.key === step);

  if (loadingSites) {
    return (
      <div className="container-fluid px-3 px-md-4" style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="d-flex flex-column align-items-center justify-content-center py-5 gap-3">
          <div className="spinner-border text-primary" role="status" aria-label="Loading" />
          <span className="text-secondary small">Loading sites…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4" style={{ maxWidth: 1100, margin: '0 auto', paddingBottom: 48 }}>
      {/* Progress */}
      <div className="d-flex flex-wrap justify-content-center align-items-center gap-2 gap-sm-3 mb-4 pb-2 border-bottom">
        {SCOPE_STEPS.map((s, i) => {
          const done = stepIndex > i;
          const active = step === s.key;
          return (
            <React.Fragment key={s.key}>
              <div className="d-flex flex-column align-items-center gap-1" style={{ minWidth: 72 }}>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center fw-bold small"
                  style={{
                    width: 40,
                    height: 40,
                    background: done ? '#059669' : active ? '#1a56db' : '#e5e7eb',
                    color: done || active ? '#fff' : '#6b7280',
                  }}
                  aria-current={active ? 'step' : undefined}
                >
                  {done ? <i className="bi bi-check-lg" aria-hidden="true" /> : <i className={`bi ${s.icon}`} aria-hidden="true" />}
                </div>
                <span className={`small ${active ? 'text-primary fw-bold' : done ? 'text-success' : 'text-muted'}`} style={{ fontSize: 11 }}>
                  {s.label}
                </span>
              </div>
              {i < SCOPE_STEPS.length - 1 && (
                <div className="d-none d-sm-block flex-grow-1" style={{ height: 2, maxWidth: 48, background: done ? '#a7f3d0' : '#e5e7eb', marginBottom: 22 }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Site (ship-to context) ── */}
      {step === 'site' && (
        <div className="row g-4">
          <div className="col-12 text-center mb-1">
            <h2 className="h5 fw-bold text-dark mb-2">
              <i className="bi bi-geo-alt me-2 text-primary" aria-hidden="true" />
              Select ExtremeB2B storefront site
            </h2>
            <p className="text-secondary small mb-0 px-md-5">
              This is the ecommerce website (site UUID). It is not the same as a checkout ship-to / delivery address — that is configured later when you limit by ship-to addresses.
            </p>
          </div>

          <div className="col-12 col-lg-10 offset-lg-1">
            <label className="form-label small fw-semibold text-muted" htmlFor="scope-site-search">Find a site</label>
            <div className="input-group">
              <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted" aria-hidden="true" /></span>
              <input
                id="scope-site-search"
                className="form-control border-start-0"
                placeholder="Search name, URL, or ID…"
                value={siteSearch}
                onChange={e => setSiteSearch(e.target.value)}
                autoComplete="off"
              />
            </div>
            {siteSearch && (
              <div className="form-text">{filteredSites.length} match{filteredSites.length !== 1 ? 'es' : ''}</div>
            )}
          </div>

          {sites.length === 0 && (
            <div className="col-12">
              <div className="text-center py-5 rounded-3 border border-dashed bg-light text-muted">
                <i className="bi bi-globe d-block mb-2 fs-2 opacity-50" aria-hidden="true" />
                <div className="fw-semibold">No sites found</div>
                <div className="small mt-1">Create a site under Customer Identity first.</div>
              </div>
            </div>
          )}

          <div className="row g-3">
            {filteredSites.map(site => (
              <div key={site.id} className="col-12 col-md-6 col-xl-4">
                <button
                  type="button"
                  className={`card h-100 shadow-sm border-2 ${selectedSite?.id === site.id ? 'border-primary bg-primary-subtle' : 'border-light'}`}
                  style={{ ...cardBase, marginBottom: 0, cursor: 'pointer' }}
                  onClick={() => handleSiteSelect(site)}
                >
                  <div className="d-flex align-items-start gap-3">
                    <div
                      className="rounded-3 d-flex align-items-center justify-content-center flex-shrink-0"
                      style={{
                        width: 44,
                        height: 44,
                        background: selectedSite?.id === site.id ? '#dbeafe' : '#f3f4f6',
                      }}
                    >
                      <i className={`bi bi-globe fs-5 ${selectedSite?.id === site.id ? 'text-primary' : 'text-secondary'}`} aria-hidden="true" />
                    </div>
                    <div className="flex-grow-1 text-start overflow-hidden">
                      <div className="fw-bold text-dark small text-truncate">
                        {site.name}
                        {site.is_active === false && (
                          <span className="badge bg-secondary-subtle text-secondary ms-2 text-uppercase" style={{ fontSize: 9 }}>inactive</span>
                        )}
                      </div>
                      {site.url && <div className="text-muted text-truncate" style={{ fontSize: 12 }}>{site.url}</div>}
                      <code className="text-primary" style={{ fontSize: 11 }}>{site.id}</code>
                    </div>
                    <i className="bi bi-chevron-right text-muted flex-shrink-0 mt-1" aria-hidden="true" />
                  </div>
                </button>
              </div>
            ))}
          </div>

          <div className="col-12 text-center mt-2">
            <button
              type="button"
              className="btn btn-link btn-sm text-muted"
              onClick={() => onConfirm({
                customer_csv: '',
                ship_to_csv: '',
                selectedSite: null,
                selectedCustomers: [],
                customerUsersMap: {},
                userScopeMode: 'all',
                selectedUsers: [],
                restriction_basis: 'users',
                delivery_scope_mode: 'all',
                delivery_ship_to_csv: '',
              })}
            >
              Skip — apply to all sites &amp; customers
            </button>
          </div>
        </div>
      )}

      {/* ── Customers ── */}
      {step === 'customer' && (
        <div className="row g-3">
          <div className="col-12 d-flex flex-wrap justify-content-between align-items-center gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep('site')}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back to sites
            </button>
            <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle">
              {selectedCustomers.length} selected
            </span>
          </div>

          <div className="col-12 text-center">
            <h2 className="h5 fw-bold text-dark mb-2">
              <i className="bi bi-building me-2 text-primary" aria-hidden="true" />
              Select customers
            </h2>
            <p className="text-secondary small mb-0">
              Site: <strong>{selectedSite?.name}</strong>
              <span className="text-muted"> · </span>
              Pick one or more customer accounts, or leave none selected for all accounts on this site.
            </p>
          </div>

          {loadingCustomers ? (
            <div className="col-12 text-center py-5">
              <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading customers" />
            </div>
          ) : (
            <>
              <div className="col-12 col-lg-10 offset-lg-1">
                <label className="form-label small fw-semibold text-muted" htmlFor="scope-cust-search">Search customers</label>
                <div className="input-group">
                  <span className="input-group-text bg-white border-end-0"><i className="bi bi-search text-muted" aria-hidden="true" /></span>
                  <input
                    id="scope-cust-search"
                    className="form-control border-start-0"
                    placeholder="Name, email, or ERP ID…"
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                  />
                </div>
              </div>

              <div className="col-12 col-lg-10 offset-lg-1 d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div className="btn-group btn-group-sm" role="group" aria-label="Bulk selection">
                  <button type="button" className="btn btn-outline-primary" onClick={selectAllFilteredCustomers} disabled={filteredCustomers.length === 0}>
                    Select all {filteredCustomers.length ? `(${filteredCustomers.length})` : ''}
                  </button>
                  <button type="button" className="btn btn-outline-secondary" onClick={clearCustomerSelection} disabled={selectedCustomers.length === 0}>
                    Clear
                  </button>
                </div>
                <span className="small text-muted">{filteredCustomers.length} shown</span>
              </div>

              {filteredCustomers.length === 0 && (
                <div className="col-12 text-center py-4 text-muted">
                  <i className="bi bi-building fs-3 d-block mb-2 opacity-50" aria-hidden="true" />
                  No customers match this search or this site has no customers yet.
                </div>
              )}

              <div className="col-12 col-lg-10 offset-lg-1">
                <div className="rounded-3 border bg-white" style={{ maxHeight: 420, overflowY: 'auto', boxShadow: 'inset 0 1px 2px rgba(0,0,0,.04)' }}>
                  {filteredCustomers.map(cust => {
                    const active = selectedCustomers.some(c => c.id === cust.id);
                    return (
                      <button
                        key={cust.id}
                        type="button"
                        className="w-100 border-0 border-bottom bg-transparent text-start px-3 py-3 d-flex align-items-center gap-3"
                        style={{
                          cursor: 'pointer',
                          background: active ? '#eff6ff' : '#fff',
                          transition: 'background .12s',
                        }}
                        onClick={() => toggleCustomer(cust)}
                      >
                        <div
                          className="rounded-2 d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{ width: 40, height: 40, background: active ? '#dbeafe' : '#f3f4f6' }}
                        >
                          <i className={`bi ${cust.account_type === 'business' ? 'bi-building' : 'bi-person'} ${active ? 'text-primary' : 'text-secondary'}`} aria-hidden="true" />
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="fw-semibold text-dark small">{cust.name}</div>
                          <div className="text-muted d-flex flex-wrap gap-2" style={{ fontSize: 12 }}>
                            {cust.erp_customer_id && <span><span className="text-secondary">ERP</span> {cust.erp_customer_id}</span>}
                            {cust.email && <span className="text-truncate">{cust.email}</span>}
                            <span className="text-capitalize">{cust.account_type}</span>
                          </div>
                          {active && customerUsers[cust.id] && (
                            <div className="mt-2 pt-2 border-top small text-muted">
                              <span className="me-3"><i className="bi bi-people me-1" aria-hidden="true" />{customerUsers[cust.id].users?.length || 0} users</span>
                              <span><i className="bi bi-truck me-1" aria-hidden="true" />{customerUsers[cust.id].addresses?.length || 0} ship-to addresses</span>
                            </div>
                          )}
                        </div>
                        <div className="flex-shrink-0">
                          {active
                            ? <i className="bi bi-check-circle-fill text-primary fs-5" aria-hidden="true" />
                            : <i className="bi bi-circle text-secondary fs-5" aria-hidden="true" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="col-12 col-lg-10 offset-lg-1 d-flex flex-column flex-sm-row gap-3 justify-content-between align-items-stretch align-items-sm-center mt-2">
                <p className="small text-muted mb-0">
                  {selectedCustomers.length > 0 ? (
                    <><i className="bi bi-check-circle-fill text-success me-1" aria-hidden="true" />{selectedCustomers.length} customer{selectedCustomers.length !== 1 ? 's' : ''} in scope — next, choose user-level vs shipping-location-level scope.</>
                  ) : (
                    <>No customer filter — program applies to <strong>all</strong> accounts on this site.</>
                  )}
                </p>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={continueFromCustomer}
                >
                  <i className="bi bi-arrow-right me-1" aria-hidden="true" />Continue
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ── Step: User-level vs shipping-location-level ── */}
      {step === 'basis' && selectedSite && (
        <div className="row g-4">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep('customer')}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
          </div>
          <div className="col-12 text-center">
            <h2 className="h5 fw-bold text-dark mb-2">
              <i className="bi bi-funnel me-2 text-primary" aria-hidden="true" />
              User-level or shipping-location-level program?
            </h2>
            <p className="text-secondary small mb-0 px-md-5">
              Decide how eligibility is enforced before picking specific users or ship-to addresses.
            </p>
          </div>
          <div className="col-12 col-lg-8 offset-lg-2">
            <div className="row g-2">
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  className={`btn w-100 py-3 ${restrictionBasis === 'delivery' ? 'btn-outline-primary' : 'btn-primary'}`}
                  onClick={() => {
                    setRestrictionBasis('users');
                    setDeliveryScopeMode('all');
                    setDeliveryShipToCsv('');
                  }}
                >
                  <i className="bi bi-people-fill d-block mb-1 fs-5" aria-hidden="true" />
                  User-level program
                </button>
              </div>
              <div className="col-12 col-md-6">
                <button
                  type="button"
                  className={`btn w-100 py-3 ${restrictionBasis === 'delivery' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => {
                    setRestrictionBasis('delivery');
                    setUserScopeMode('all');
                    setSelectedUsers([]);
                  }}
                >
                  <i className="bi bi-truck d-block mb-1 fs-5" aria-hidden="true" />
                  Shipping-location-level program
                </button>
              </div>
            </div>
          </div>
          <div className="col-12 text-center mt-3">
            <button type="button" className="btn btn-primary px-4" onClick={continueFromBasis}>
              <i className="bi bi-arrow-right me-1" aria-hidden="true" />Continue
            </button>
          </div>
        </div>
      )}

      {/* ── Step: Pick users or ship-to addresses ── */}
      {step === 'eligibility' && selectedSite && (
        <div className="row g-4">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep('basis')}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
          </div>
          <div className="col-12 text-center">
            <h2 className="h5 fw-bold text-dark mb-2">
              <i className="bi bi-person-badge me-2 text-primary" aria-hidden="true" />
              Refine eligibility
            </h2>
            <p className="text-secondary small mb-0">
              {restrictionBasis === 'users'
                ? 'Choose all enrolled users or only selected users under the customer accounts you picked.'
                : 'Choose all saved ship-to addresses or only selected checkout addresses.'}
            </p>
          </div>

          {restrictionBasis === 'users' && (
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="card border-info border-opacity-50 shadow-sm">
                <div className="card-body py-3">
                  <div className="btn-group w-100 mb-3" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${userScopeMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => { setUserScopeMode('all'); setSelectedUsers([]); setUserPickSearch(''); }}
                    >
                      All enrolled users
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${userScopeMode === 'select' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setUserScopeMode('select')}
                    >
                      Only selected users
                    </button>
                  </div>
                  {userScopeMode === 'select' && (
                    <>
                      <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Search users…"
                        value={userPickSearch}
                        onChange={(e) => setUserPickSearch(e.target.value)}
                      />
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllFilteredUsers} disabled={filteredUsersForPicker.length === 0}>
                          Select all ({filteredUsersForPicker.length})
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearUserPick} disabled={selectedUsers.length === 0}>
                          Clear
                        </button>
                        <span className="small text-muted align-self-center">{selectedUsers.length} selected</span>
                      </div>
                      <div className="rounded-3 border bg-white" style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {filteredUsersForPicker.map((row) => {
                          const on = selectedUsers.some((u) => u.id === row.id);
                          return (
                            <button
                              key={`${row.customer_id}-${row.id}`}
                              type="button"
                              className="w-100 border-0 border-bottom bg-transparent text-start px-3 py-2 d-flex align-items-center gap-2"
                              style={{ background: on ? '#eff6ff' : '#fff' }}
                              onClick={() => togglePickUser(row)}
                            >
                              <i className={`bi ${on ? 'bi-check-circle-fill text-primary' : 'bi-circle text-secondary'}`} aria-hidden="true" />
                              <div className="flex-grow-1 min-w-0">
                                <div className="small fw-semibold text-truncate">{row.display_name}</div>
                                <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                                  {row.email && <span>{row.email} · </span>}
                                  <span className="badge bg-light text-secondary border">{row.customer_name}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {filteredUsersForPicker.length === 0 && (
                          <div className="small text-muted text-center py-4">Loading users…</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {restrictionBasis === 'delivery' && (
            <div className="col-12 col-lg-10 offset-lg-1">
              <div className="card border-info border-opacity-50 shadow-sm">
                <div className="card-body py-3">
                  <div className="btn-group w-100 mb-3" role="group">
                    <button
                      type="button"
                      className={`btn btn-sm ${deliveryScopeMode === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => { setDeliveryScopeMode('all'); setDeliveryShipToCsv(''); }}
                    >
                      All saved addresses
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm ${deliveryScopeMode === 'select' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setDeliveryScopeMode('select')}
                    >
                      Only selected addresses
                    </button>
                  </div>
                  {deliveryScopeMode === 'select' && (
                    <>
                      <input
                        className="form-control form-control-sm mb-2"
                        placeholder="Search addresses…"
                        value={deliveryPickSearch}
                        onChange={(e) => setDeliveryPickSearch(e.target.value)}
                      />
                      <div className="d-flex flex-wrap gap-2 mb-2">
                        <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllFilteredAddr} disabled={filteredAddrPick.length === 0}>
                          Select all ({filteredAddrPick.length})
                        </button>
                        <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearAddrPick} disabled={selectedDeliveryIds.length === 0}>
                          Clear
                        </button>
                        <span className="small text-muted align-self-center">{selectedDeliveryIds.length} selected</span>
                      </div>
                      <div className="rounded-3 border bg-white" style={{ maxHeight: 280, overflowY: 'auto' }}>
                        {filteredAddrPick.map((row) => {
                          const on = selectedDeliveryIds.includes(String(row.id));
                          return (
                            <button
                              key={`${row.customer_id}-${row.id}`}
                              type="button"
                              className="w-100 border-0 border-bottom bg-transparent text-start px-3 py-2 d-flex align-items-center gap-2"
                              style={{ background: on ? '#eff6ff' : '#fff' }}
                              onClick={() => togglePickAddr(row)}
                            >
                              <i className={`bi ${on ? 'bi-check-circle-fill text-primary' : 'bi-circle text-secondary'}`} aria-hidden="true" />
                              <div className="flex-grow-1 min-w-0">
                                <div className="small fw-semibold text-truncate">{row.label}</div>
                                <div className="text-muted text-truncate" style={{ fontSize: 11 }}>
                                  {row.summary} · <span className="badge bg-light text-secondary border">{row.customer_name}</span>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                        {filteredAddrPick.length === 0 && (
                          <div className="small text-muted text-center py-4">Select customers with saved addresses.</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="col-12 text-center mt-2">
            <button type="button" className="btn btn-primary px-4" onClick={continueFromEligibility}>
              <i className="bi bi-arrow-right me-1" aria-hidden="true" />Continue to review
            </button>
          </div>
        </div>
      )}

      {/* ── Review + users / ship-to preview ── */}
      {step === 'review' && (
        <div className="row g-4">
          <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setStep('eligibility')}>
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Back
            </button>
          </div>

          <div className="col-12 text-center">
            <h2 className="h5 fw-bold text-dark mb-2">Confirm scope</h2>
            <p className="text-secondary small mb-0">Ship-to, customers, and who is covered under those accounts.</p>
          </div>

          <div className="col-12 col-md-6">
            <div className="card h-100 border-primary border-opacity-25 shadow-sm">
              <div className="card-header py-2 small fw-bold text-primary bg-primary-subtle border-0">
                <i className="bi bi-geo-alt me-1" aria-hidden="true" />Storefront site ID
              </div>
              <div className="card-body py-3">
                <div className="fw-semibold">{selectedSite?.name}</div>
                {selectedSite?.url && <div className="small text-muted">{selectedSite.url}</div>}
                <code className="small d-block mt-2 text-primary">{selectedSite?.id}</code>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="card h-100 border-success border-opacity-25 shadow-sm">
              <div className="card-header py-2 small fw-bold text-success bg-success-subtle border-0">
                <i className="bi bi-building me-1" aria-hidden="true" />Customers ({selectedCustomers.length})
              </div>
              <div className="card-body py-3" style={{ maxHeight: 220, overflowY: 'auto' }}>
                {selectedCustomers.map(c => (
                  <div key={c.id} className="d-flex gap-2 mb-2 pb-2 border-bottom border-light">
                    <i className="bi bi-check-circle-fill text-success flex-shrink-0 mt-1" aria-hidden="true" />
                    <div>
                      <div className="fw-semibold small">{c.name}</div>
                      <div className="text-muted" style={{ fontSize: 11 }}>
                        {c.erp_customer_id && `ERP ${c.erp_customer_id}`}
                        {customerUsers[c.id] && (
                          <span className="ms-2">
                            · {customerUsers[c.id].users?.length || 0} users · {customerUsers[c.id].addresses?.length || 0} addresses
                          </span>
                        )}
                        {loadingUsers && !customerUsers[c.id] && <span className="spinner-border spinner-border-sm ms-1" style={{ width: 10, height: 10 }} role="status" />}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {((restrictionBasis === 'users' && selectedCustomers.length > 0) || restrictionBasis === 'delivery') && (
            <div className="col-12">
              {restrictionBasis === 'users' ? (
                <div className={`alert ${userScopeMode === 'select' ? 'alert-warning border-warning' : 'alert-info border-info'} py-3 small mb-0`}>
                  <div className="fw-bold mb-1">
                    <i className="bi bi-person-badge me-1" aria-hidden="true" />
                    Program access (users)
                  </div>
                  {userScopeMode === 'all' ? (
                    <span>All enrolled users under the selected customers may use this program ({reviewCoverageSummary.userCount} user{reviewCoverageSummary.userCount !== 1 ? 's' : ''} shown below).</span>
                  ) : (
                    <span>
                      Restricted to <strong>{selectedUsers.length}</strong> selected user{selectedUsers.length !== 1 ? 's' : ''}.
                      Others under these customers will not see the program.
                    </span>
                  )}
                </div>
              ) : (
                <div className={`alert ${deliveryScopeMode === 'select' ? 'alert-warning border-warning' : 'alert-info border-info'} py-3 small mb-0`}>
                  <div className="fw-bold mb-1">
                    <i className="bi bi-truck me-1" aria-hidden="true" />
                    Program access (shipping addresses)
                  </div>
                  {deliveryScopeMode === 'all' ? (
                    <span>All saved ship-to addresses for the selected customers qualify at checkout.</span>
                  ) : (
                    <span>
                      Restricted to <strong>{selectedDeliveryIds.length}</strong> selected ship-to address{selectedDeliveryIds.length !== 1 ? 'es' : ''}.
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="col-12">
            <div className="card border-secondary border-opacity-25 shadow-sm">
              <div className="card-header py-2 d-flex flex-wrap justify-content-between align-items-center gap-2 bg-light border-bottom">
                <span className="small fw-bold text-dark">
                  <i className="bi bi-people me-1 text-primary" aria-hidden="true" />
                  Users &amp; ship-to addresses
                  <span className="fw-normal text-muted ms-2">
                    ({reviewCoverageSummary.userCount} users · {reviewCoverageSummary.addrCount} addresses)
                  </span>
                </span>
              </div>
              <div className="card-body">
                <label className="form-label small text-muted" htmlFor="scope-review-user-search">Filter preview</label>
                <input
                  id="scope-review-user-search"
                  className="form-control form-control-sm mb-3"
                  placeholder="Search user name, email, city, or label…"
                  value={reviewUserSearch}
                  onChange={e => setReviewUserSearch(e.target.value)}
                />

                {selectedCustomers.map(c => {
                  const pack = customerUsers[c.id];
                  const q = reviewUserSearch.trim().toLowerCase();
                  const users = (pack?.users || []).filter(u => {
                    if (!q) return true;
                    return `${u.display_name || ''} ${u.email || ''} ${u.role || ''}`.toLowerCase().includes(q);
                  });
                  const addrs = (pack?.addresses || []).filter(a => {
                    if (!q) return true;
                    return `${a.label || ''} ${a.city || ''} ${a.address_line1 || ''} ${a.postal_code || ''}`.toLowerCase().includes(q);
                  });
                  if (q && users.length === 0 && addrs.length === 0 && pack) return null;
                  if (!pack && loadingUsers) {
                    return (
                      <div key={c.id} className="mb-4 pb-3 border-bottom">
                        <div className="fw-semibold small mb-2">{c.name}</div>
                        <div className="spinner-border spinner-border-sm text-primary" role="status" />
                      </div>
                    );
                  }
                  if (!pack) return null;
                  return (
                    <div key={c.id} className="mb-4 pb-3 border-bottom border-light">
                      <div className="fw-bold small text-dark mb-2">
                        <i className="bi bi-building me-1 text-secondary" aria-hidden="true" />
                        {c.name}
                      </div>
                      <div className="row g-2">
                        <div className="col-12 col-lg-6">
                          <div className="text-uppercase text-muted mb-1" style={{ fontSize: 10, letterSpacing: '.06em' }}>Users</div>
                          <div className="d-flex flex-wrap gap-1">
                            {users.length === 0 && <span className="text-muted small">No matches</span>}
                            {users.map(u => (
                              <span key={u.id} className="badge bg-light text-dark border fw-normal text-wrap text-start" style={{ maxWidth: '100%' }}>
                                {u.display_name || u.email}
                                {u.email && u.display_name && <span className="text-muted d-block small">{u.email}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="col-12 col-lg-6">
                          <div className="text-uppercase text-muted mb-1" style={{ fontSize: 10, letterSpacing: '.06em' }}>Ship-to addresses</div>
                          <ul className="list-unstyled small mb-0">
                            {addrs.length === 0 && <li className="text-muted">No matches</li>}
                            {addrs.map(a => (
                              <li key={a.id} className="mb-1 ps-2 border-start border-3 border-primary border-opacity-25">
                                <strong>{a.label || 'Address'}</strong>
                                <span className="text-muted">
                                  {' '}— {[a.city, a.state_province].filter(Boolean).join(', ')} {a.postal_code}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="col-12">
            <button type="button" className="btn btn-primary btn-lg w-100 py-3 fw-bold" onClick={handleConfirm}>
              <i className="bi bi-arrow-right me-2" aria-hidden="true" />Continue to program setup
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_SCOPING = {
  customer_csv: '',
  ship_to_csv: '',
  selectedSite: null,
  selectedCustomers: [],
  customerUsersMap: {},
  userScopeMode: 'all',
  selectedUsers: [],
  restriction_basis: 'users',
  delivery_scope_mode: 'all',
  delivery_ship_to_csv: '',
};

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function ProgramCreatePage({ onCreated }) {
  const navigate = useNavigate();
  // Flow: 'picker' → ('scoping' → 'wizard') | 'ai'
  // Method picker is shown first so AI Assistant is reachable in one click.
  const [phase, setPhase] = useState('picker');
  const [scoping, setScoping] = useState(EMPTY_SCOPING);
  const [productLists, setProductLists] = useState([]);
  const [userGroups, setUserGroups] = useState([]);

  useEffect(() => {
    Promise.all([listProductLists(), listUserGroups()]).then(([pl, ug]) => {
      setProductLists(pl?.items || []);
      setUserGroups(ug?.items || []);
    }).catch(() => {});
  }, []);

  const handlePickMethod = useCallback((key) => {
    if (key === 'ai') {
      setScoping(EMPTY_SCOPING);
      setPhase('ai');
    } else if (key === 'wizard') {
      setPhase('scoping');
    }
  }, []);

  const handleScopingConfirm = useCallback((sel) => {
    setScoping(sel);
    setPhase('wizard');
  }, []);

  const handleFinish = useCallback(async (payload) => {
    // Merge scope: wizard/classic/AI may produce customer + ship_to + user on payload.assignments.
    // Prefer those when they reflect real scope; otherwise use pre-flight scoping from step 1.
    const existing = payload.assignments || [];
    const scopeEntityTypes = ['customer', 'ship_to', 'user', 'delivery_ship_to'];
    const withoutScope = existing.filter((a) => !scopeEntityTypes.includes(a.entity_type));
    const scopeRows = existing.filter((a) => scopeEntityTypes.includes(a.entity_type));
    const wizardHasCustomers = scopeRows.some(
      (a) => a.entity_type === 'customer' && String(a.entity_key || '').trim()
        && String(a.entity_key) !== 'self',
    );
    const wizardHasShipTos = scopeRows.some(
      (a) => a.entity_type === 'ship_to' && String(a.entity_key || '').trim(),
    );
    const wizardHasUsers = scopeRows.some((a) => a.entity_type === 'user' && String(a.entity_key || '').trim());
    const wizardHasDelivery = scopeRows.some(
      (a) => a.entity_type === 'delivery_ship_to' && String(a.entity_key || '').trim(),
    );

    let scopeAssignments;
    if (wizardHasCustomers || wizardHasShipTos || wizardHasUsers || wizardHasDelivery) {
      scopeAssignments = scopeRows;
    } else {
      const customerKeys = (scoping.customer_csv || '').split(',').map(v => v.trim()).filter(Boolean);
      const shipToKeys = (scoping.ship_to_csv || '').split(',').map(v => v.trim()).filter(Boolean);
      const userRows =
        scoping.restriction_basis === 'users' && scoping.userScopeMode === 'select' && scoping.selectedUsers?.length
          ? scoping.selectedUsers.map((u) => ({ entity_type: 'user', entity_key: String(u.id) }))
          : [];
      const deliveryRows =
        scoping.restriction_basis === 'delivery' && scoping.delivery_scope_mode === 'select'
          ? splitCsv(scoping.delivery_ship_to_csv).map((id) => ({ entity_type: 'delivery_ship_to', entity_key: id }))
          : [];
      scopeAssignments = [
        ...(customerKeys.length ? customerKeys.map(k => ({ entity_type: 'customer', entity_key: k })) : [{ entity_type: 'customer', entity_key: 'self' }]),
        ...shipToKeys.map(k => ({ entity_type: 'ship_to', entity_key: k })),
        ...userRows,
        ...deliveryRows,
      ];
    }
    const merged = { ...payload, assignments: [...withoutScope, ...scopeAssignments] };

    try {
      await createAdminProgram(merged);
      toast.success('Program created!');
      if (typeof onCreated === 'function') {
        await onCreated();
      }
      navigate('/purchase-program');
    } catch (err) {
      toast.error(err.message || 'Failed to create program');
    }
  }, [navigate, scoping, onCreated]);

  const scopingBadge = () => {
    const parts = [];
    if (scoping.selectedSite?.name) parts.push(`Site: ${scoping.selectedSite.name}`);
    else if (scoping.ship_to_csv)   parts.push(`Site: ${scoping.ship_to_csv}`);
    if (scoping.selectedCustomers?.length > 0) {
      const names = scoping.selectedCustomers.map(c => c.name || c.id).join(', ');
      parts.push(`Customers: ${names}`);
    } else if (scoping.customer_csv) {
      parts.push(`Customers: ${scoping.customer_csv}`);
    }
    if (scoping.restriction_basis === 'delivery') {
      parts.push(
        scoping.delivery_scope_mode === 'select' && splitCsv(scoping.delivery_ship_to_csv).length
          ? `Ship-to: ${splitCsv(scoping.delivery_ship_to_csv).length} address(es)`
          : 'Ship-to: all addresses',
      );
    } else if (scoping.userScopeMode === 'select' && scoping.selectedUsers?.length > 0) {
      parts.push(`Users: ${scoping.selectedUsers.length} selected`);
    } else if (scoping.selectedCustomers?.length > 0 || scoping.customer_csv) {
      parts.push('Users: all under customer(s)');
    }
    return parts.join(' · ') || 'All sites & customers';
  };

  const isWizardPath = phase === 'scoping' || phase === 'wizard';
  const isAiPath = phase === 'ai';
  const headerTitle = phase === 'picker'
    ? 'Create New Program'
    : `New Program — ${MODES.find(m => m.key === (isAiPath ? 'ai' : 'wizard'))?.title || ''}`;

  // Breadcrumb steps adapt to the chosen path:
  //   Method → Configure                       (AI)
  //   Method → Scope → Configure               (Wizard)
  const stepDots = (() => {
    const steps = [];
    steps.push({
      key: 'method',
      label: 'Method',
      active: phase === 'picker',
      done: phase !== 'picker',
    });
    if (isWizardPath || phase === 'picker') {
      steps.push({
        key: 'scope',
        label: 'Scope',
        active: phase === 'scoping',
        done: phase === 'wizard',
        muted: phase === 'picker',
      });
    }
    steps.push({
      key: 'configure',
      label: 'Configure',
      active: phase === 'wizard' || phase === 'ai',
      done: false,
      muted: phase === 'picker' || phase === 'scoping',
    });
    return steps;
  })();

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Sticky header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', padding: '16px 24px', position: 'sticky', top: 0, zIndex: 100 }}>
        <NavLink to="/purchase-program" style={{ fontSize: 12, color: '#6b7280', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 6 }}>
          <i className="bi bi-arrow-left" aria-hidden="true" /> Back to Programs
        </NavLink>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#111827', margin: 0 }}>
              <i className="bi bi-bag-plus-fill me-2" style={{ color: '#1a56db' }} aria-hidden="true" />
              {headerTitle}
            </h1>
            {/* Breadcrumb trail */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
              {stepDots.map((s, i) => (
                <React.Fragment key={s.key}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <div style={{
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      background: s.done ? '#10b981' : s.active ? '#1a56db' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 11,
                      color: s.muted && !s.active ? '#9ca3af' : '#fff',
                      fontWeight: 700,
                    }}>
                      {s.done ? <i className="bi bi-check-lg" style={{ fontSize: 9 }} /> : (i + 1)}
                    </div>
                    <span style={{
                      fontSize: 12,
                      color: s.active ? '#1a56db' : s.done ? '#6b7280' : '#9ca3af',
                      fontWeight: s.active ? 700 : 400,
                    }}>
                      {s.label}
                    </span>
                  </div>
                  {i < stepDots.length - 1 && (
                    <i className="bi bi-chevron-right" style={{ fontSize: 10, color: '#d1d5db' }} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
          {/* Scoping badge — only meaningful for the wizard path after preflight */}
          {phase === 'wizard' && (
            <div style={{ fontSize: 12, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '6px 12px', color: '#0369a1', display: 'flex', alignItems: 'center', gap: 6 }}>
              <i className="bi bi-building" />
              {scopingBadge()}
              <button type="button" style={{ background: 'none', border: 'none', color: '#0369a1', cursor: 'pointer', padding: 0, marginLeft: 4, fontSize: 11, textDecoration: 'underline' }}
                onClick={() => setPhase('scoping')}>Change</button>
            </div>
          )}
        </div>
      </div>

      {/* Phase 1: Method picker — shown first so AI Assistant is one click away */}
      {phase === 'picker' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '40px 24px', gap: 16, maxWidth: 620, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 8 }}>
            <p style={{ fontSize: 14, color: '#6b7280', margin: 0 }}>Choose how you want to set up this purchase program.</p>
          </div>
          {MODES.map(({ key, icon, color, bg, title, desc }) => (
            <button key={key} type="button" onClick={() => handlePickMethod(key)}
              style={{ width: '100%', textAlign: 'left', border: `2px solid ${color}33`, borderRadius: 14, padding: '18px 22px', background: '#fff', cursor: 'pointer', display: 'flex', gap: 16, alignItems: 'flex-start', transition: 'all .15s', boxShadow: '0 1px 4px rgba(0,0,0,.05)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = color; e.currentTarget.style.background = bg; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = `${color}33`; e.currentTarget.style.background = '#fff'; }}>
              <div style={{ width: 48, height: 48, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <i className={`bi ${icon}`} style={{ fontSize: 24, color }} aria-hidden="true" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 4 }}>{title}</div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>{desc}</div>
              </div>
              <i className="bi bi-chevron-right" style={{ color: '#d1d5db', fontSize: 18, alignSelf: 'center' }} aria-hidden="true" />
            </button>
          ))}
        </div>
      )}

      {/* Phase 2 (wizard path only): Scoping preflight */}
      {phase === 'scoping' && (
        <>
          <div style={{ maxWidth: 1100, margin: '12px auto 0', padding: '0 16px' }}>
            <button
              type="button"
              className="btn btn-link btn-sm text-secondary p-0"
              onClick={() => setPhase('picker')}
            >
              <i className="bi bi-arrow-left me-1" aria-hidden="true" />Change method
            </button>
          </div>
          <ScopingPreflight onConfirm={handleScopingConfirm} />
        </>
      )}

      {/* Phase 3a: Wizard */}
      {phase === 'wizard' && (
        <div style={{ maxWidth: 700, margin: '32px auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.08)', overflow: 'hidden', minHeight: 500 }}>
          <ProgramCreationWizard
            productLists={productLists}
            userGroups={userGroups}
            initialScoping={scoping}
            onFinish={handleFinish}
            onCancel={() => setPhase('picker')}
          />
        </div>
      )}

      {/* Phase 3b: AI Assistant — reachable in one click from the picker */}
      {phase === 'ai' && (
        <div style={{ maxWidth: 860, margin: '32px auto', background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,.08)', overflow: 'hidden', height: 'calc(100vh - 160px)', display: 'flex', flexDirection: 'column' }}>
          <AIProgramCreator
            productLists={productLists}
            userGroups={userGroups}
            scoping={scoping}
            onFinish={handleFinish}
            onCancel={() => setPhase('picker')}
          />
        </div>
      )}
    </div>
  );
}
